import { Link } from "@tanstack/react-router";
import { FileLock2, ExternalLink } from "lucide-react";
import { KENTRO } from "@/lib/constants";
import { useLocale } from "@/i18n/LocaleProvider";
import type { Locale } from "@/i18n/types";

const DPO_URL = "https://www.privacidade.com.br/portal-de-privacidade?token=88047695397ac976534ac691a2b9eb2b";

type TermDef = { term: string; def: string };

type Content = {
  badge: string;
  titleA: string; titleHighlight: string; titleEnd: string;
  meta: string;
  intro: React.ReactNode;
  dpoCta: string;
  summary: { heading: string; rows: { k: string; v: string }[] };
  tocHeading: string;
  toc: { id: string; label: string }[];
  s1: { title: string; intro: string; tableHeader: [string, string]; rows: [string, string][]; children: string; childrenNote: string; thirdHeading: string; thirdBody: string; autoHeading: string; autoBody: string };
  s2: { title: string; tableHeader: [string, string]; rows: [string, string][] };
  s3: { title: string; items: string[]; tail: string };
  s4: { title: string; items: string[]; tail: string };
  s5: { title: string; body: string };
  s6: { title: string; items: string[]; portalLink: string; via: string; or: string; rejection: string };
  s7: { title: string; body: string; version: string };
  glossary: { title: string; items: TermDef[] };
  otherDocsTitle: string;
  policyLink: string;
  ombudsmanLink: string;
};

const ptContent: Content = {
  badge: "LGPD · Versão 1.0",
  titleA: "Aviso de ", titleHighlight: "Privacidade", titleEnd: " e Proteção de Dados",
  meta: "Elaborado em 01/10/2023 · Atualizado em 09/11/2023.",
  intro: (
    <>
      A <strong className="text-foreground">Barbalho Solutions Ltda.</strong>, CNPJ 43.406.507/0001-52, com sede na Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP — CEP 06454-000 ("Controlador"), regula neste Aviso, de forma simples, transparente e objetiva, como seus dados pessoais ("Titular") serão coletados, utilizados e protegidos.
    </>
  ),
  dpoCta: "Portal do Encarregado de Proteção de Dados (DPO)",
  summary: {
    heading: "Quadro resumo",
    rows: [
      { k: "Agente de tratamento", v: "Barbalho Solutions Ltda." },
      { k: "Papel no tratamento", v: "Controlador e Operador" },
      { k: "Natureza dos dados", v: "Dados pessoais fornecidos pelo Titular, de natureza cadastral, financeira e sensível (art. 5º, II da Lei 13.709/2018)." },
      { k: "Compartilhamento", v: "Operadores e fornecedores essenciais; autoridades/órgãos governamentais por força legal ou regulatória." },
      { k: "Proteção", v: "Medidas técnicas e administrativas adequadas à natureza dos dados tratados." },
      { k: "Seus direitos", v: "Confirmação, acesso, anonimização, correção, eliminação, portabilidade, bloqueio, oposição e compartilhamento." },
    ],
  },
  tocHeading: "Sumário",
  toc: [
    { id: "dados", label: "Quais dados são coletados sobre você" },
    { id: "uso", label: "Como utilizamos seus dados pessoais" },
    { id: "compartilhamento", label: "Com quem compartilhamos seus dados pessoais" },
    { id: "seguranca", label: "Como mantemos seus dados pessoais seguros" },
    { id: "retencao", label: "Por quanto tempo seus dados serão armazenados" },
    { id: "direitos", label: "Seus direitos como titular e como exercê-los" },
    { id: "legislacao", label: "Legislação aplicável e alterações" },
    { id: "glossario", label: "Glossário" },
  ],
  s1: {
    title: "1. Quais dados são coletados sobre você",
    intro: "Para prestar serviços aos nossos clientes ou contratar colaboradores, coletamos algumas informações sobre você:",
    tableHeader: ["Serviço", "Dados pessoais"],
    rows: [
      ["Recebimento de currículos", "Nome, idade, endereço, telefone, e-mail, qualificação profissional, currículo."],
      ["Entrevista de emprego", "Nome, telefone, qualificação profissional, currículo."],
      ["Contratação de colaboradores", "Nome, cônjuge e dependentes (se houver), pai, mãe, CPF, RG, CTPS, e-mail, data de nascimento, endereço, telefone, PIS, dados de saúde (ASO), salário e dados bancários."],
      ["Wi-Fi para clientes", "Endereço MAC, IP de conexão."],
      ["Prospecção de clientes", "Nome, telefone e e-mail."],
    ],
    children: "Dados fornecidos diretamente por você",
    childrenNote: "Em alguns casos será necessário coletar dados de crianças que possuam vínculo parental com você ou estejam sob sua responsabilidade legal — nesses casos será requerido consentimento expresso. É seu dever prestar informações corretas e atualizadas.",
    thirdHeading: "Dados fornecidos por terceiros",
    thirdBody: "Conforme permitido por lei, podemos receber dados de parceiros, prestadores e outras fontes independentes, inclusive bases de dados públicas, para viabilizar a prestação dos nossos serviços.",
    autoHeading: "Dados coletados automaticamente",
    autoBody: "Coletamos também, de forma automática, dados de conexão Wi-Fi e dados de navegação (cookies).",
  },
  s2: {
    title: "2. Como utilizamos seus dados pessoais",
    tableHeader: ["Base legal", "Finalidade"],
    rows: [
      ["Execução de contratos ou diligências preliminares (art. 7º, V, LGPD)", "Recebimento de currículo; entrevista de emprego; contratação; prospecção de clientes."],
      ["Cumprimento de obrigação legal ou regulatória (art. 7º, II, LGPD)", "Guarda de registros de acesso ao Wi-Fi (art. 13 do Marco Civil); guarda de documentos trabalhistas e previdenciários."],
      ["Consentimento (art. 7º, I, LGPD)", "Tratamento de dados de crianças; contatos externos de interesse do titular via mensagens eletrônicas."],
    ],
  },
  s3: {
    title: "3. Com quem compartilharemos seus dados pessoais",
    items: [
      "Com prestadores de serviço para a contratação de funcionários.",
      "Com órgãos governamentais para cumprimento de obrigação legal.",
      "Em transações ou alterações societárias envolvendo o Controlador, para continuidade dos serviços.",
      "Mediante ordem judicial ou requerimento de autoridades administrativas competentes.",
    ],
    tail: "Todos os prestadores estão sujeitos a contratos rigorosos que proíbem o uso ou divulgação não autorizada dos dados pessoais aos quais têm acesso.",
  },
  s4: {
    title: "4. Como mantemos seus dados pessoais seguros",
    items: [
      "Proteção contra acesso não autorizado aos sistemas.",
      "Acesso restrito de pessoas específicas aos locais de armazenamento.",
      "Compromisso de sigilo absoluto por agentes, funcionários e parceiros que tratam dados.",
      "Programa de governança em privacidade, gerido pelo Encarregado pela Proteção de Dados Pessoais (DPO).",
    ],
    tail: "Embora o Controlador adote os melhores esforços, nenhuma transmissão é totalmente segura. Em caso de suspeita de quebra de segurança, podemos suspender total ou parcialmente os serviços sem aviso prévio e garantiremos transparência sobre o ocorrido.",
  },
  s5: {
    title: "5. Por quanto tempo seus dados serão armazenados",
    body: "Seus dados serão armazenados pelo período estritamente necessário ao atendimento de cada finalidade e/ou conforme os respectivos prazos legais e regulatórios.",
  },
  s6: {
    title: "6. Seus direitos como titular e como exercê-los",
    items: [
      "Confirmação da existência de tratamento.",
      "Acesso aos dados.",
      "Correção de dados incompletos, inexatos ou desatualizados.",
      "Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade.",
      "Portabilidade dos dados a outro fornecedor, mediante requisição expressa.",
      "Eliminação dos dados tratados com consentimento.",
      "Informações sobre as entidades públicas ou privadas com as quais o Controlador compartilhou seus dados.",
      "Informação sobre a possibilidade de não fornecer consentimento e suas consequências.",
      "Revogação do consentimento.",
    ],
    portalLink: "Portal de Privacidade",
    via: "As requisições são gratuitas e submetidas a uma validação de identidade. Para exercer seus direitos, fale com nosso DPO pelo ",
    or: " ou pelo e-mail ",
    rejection: "Sua requisição pode ser legalmente rejeitada por motivos formais (ex.: impossibilidade de comprovação de identidade) ou legais (ex.: pedido de exclusão de dados cuja manutenção é livre exercício de direito). Nesses casos, apresentaremos justificativas razoáveis.",
  },
  s7: {
    title: "7. Legislação aplicável e alterações",
    body: "Este documento foi elaborado com base na Lei Federal nº 13.709/2018 — Lei Geral de Proteção de Dados (LGPD). O Controlador reserva-se o direito, a seu exclusivo critério, de modificar, alterar, acrescentar ou remover partes deste documento a qualquer momento.",
    version: "Elaborado em: 01/10/2023 · Atualizado em: 09/11/2023 · Versão 1.0",
  },
  glossary: {
    title: "Glossário",
    items: [
      { term: "Anonimização", def: "Processo pelo qual o dado perde a possibilidade de associação, direta ou indireta, a um indivíduo, considerados os meios técnicos razoáveis e disponíveis no momento do tratamento." },
      { term: "ANPD", def: "Autoridade Nacional de Proteção de Dados: órgão público responsável por zelar, implementar e fiscalizar o cumprimento da LGPD em todo o território nacional." },
      { term: "Banco de dados", def: "Conjunto estruturado de dados pessoais, estabelecido em um ou vários locais, em suporte eletrônico ou físico." },
      { term: "Controlador", def: "Pessoa natural ou jurídica, de direito público ou privado, a quem competem as decisões referentes ao tratamento de dados pessoais." },
      { term: "Consentimento", def: "Manifestação livre, informada e inequívoca pela qual o titular concorda com o tratamento de seus dados pessoais para uma finalidade determinada." },
      { term: "Cookies", def: "Arquivos enviados pelo servidor ao computador do usuário para identificá-lo e obter dados de acesso, permitindo personalizar a navegação." },
      { term: "Dado pessoal", def: "Qualquer informação relacionada a pessoa natural identificada ou identificável (nome, endereço, e-mail, IP, telefone, CPF, entre outros)." },
      { term: "Dado pessoal sensível", def: "Categoria especial de dados referentes a origem racial ou étnica, convicção religiosa, opinião política, filiação sindical, religião, saúde, vida sexual, dados genéticos ou biométricos." },
      { term: "IP", def: "Internet Protocol — conjunto de números que identifica o computador do usuário na internet." },
      { term: "Operador", def: "Pessoa natural ou jurídica que realiza o tratamento de dados pessoais em nome do controlador." },
      { term: "Titular", def: "Pessoa natural a quem se referem os dados pessoais (clientes, colaboradores, parceiros e terceiros)." },
      { term: "Tratamento", def: "Toda operação realizada com dados pessoais: coleta, produção, recepção, classificação, utilização, acesso, reprodução, transmissão, distribuição, processamento, arquivamento, armazenamento, eliminação, avaliação, controle, modificação, comunicação, transferência, difusão ou extração." },
    ],
  },
  otherDocsTitle: "Outros documentos",
  policyLink: "Política de Privacidade da Plataforma",
  ombudsmanLink: "Canal de Denúncias",
};

const enContent: Content = {
  badge: "LGPD · Version 1.0",
  titleA: "Privacy ", titleHighlight: "Notice", titleEnd: " and Data Protection",
  meta: "Drafted on 10/01/2023 · Updated on 11/09/2023.",
  intro: (
    <>
      <strong className="text-foreground">Barbalho Solutions Ltda.</strong>, taxpayer ID 43.406.507/0001-52, headquartered at Alameda Rio Negro, 503, Suite 2020, Alphaville, Barueri/SP — Brazil, ZIP 06454-000 ("Controller"), regulates in this Notice, in a simple, transparent and objective way, how your personal data ("Data Subject") will be collected, used and protected.
    </>
  ),
  dpoCta: "Data Protection Officer (DPO) Portal",
  summary: {
    heading: "Summary",
    rows: [
      { k: "Processing agent", v: "Barbalho Solutions Ltda." },
      { k: "Role in processing", v: "Controller and Processor" },
      { k: "Nature of data", v: "Personal data provided by the Data Subject, of registration, financial and sensitive nature (art. 5, II, Law 13.709/2018)." },
      { k: "Sharing", v: "Essential processors and suppliers; government authorities by legal or regulatory requirement." },
      { k: "Protection", v: "Technical and administrative measures appropriate to the nature of the data processed." },
      { k: "Your rights", v: "Confirmation, access, anonymization, correction, deletion, portability, blocking, objection and sharing." },
    ],
  },
  tocHeading: "Contents",
  toc: [
    { id: "dados", label: "What data we collect about you" },
    { id: "uso", label: "How we use your personal data" },
    { id: "compartilhamento", label: "Who we share your data with" },
    { id: "seguranca", label: "How we keep your data secure" },
    { id: "retencao", label: "How long your data is stored" },
    { id: "direitos", label: "Your rights as a data subject" },
    { id: "legislacao", label: "Applicable law and changes" },
    { id: "glossario", label: "Glossary" },
  ],
  s1: {
    title: "1. What data we collect about you",
    intro: "To provide services to our customers or hire employees, we collect some information about you:",
    tableHeader: ["Service", "Personal data"],
    rows: [
      ["Receiving CVs", "Name, age, address, phone, email, professional qualifications, CV."],
      ["Job interview", "Name, phone, professional qualifications, CV."],
      ["Hiring employees", "Name, spouse and dependents (if any), parents, taxpayer IDs, RG, work card, email, date of birth, address, phone, social security, health data (medical certificate), salary and bank details."],
      ["Wi-Fi for customers", "MAC address, connection IP."],
      ["Customer prospecting", "Name, phone and email."],
    ],
    children: "Data you provide directly",
    childrenNote: "In some cases we may collect data of children with a parental link to you or under your legal responsibility — in such cases express consent will be required. It is your duty to provide correct and up-to-date information.",
    thirdHeading: "Data provided by third parties",
    thirdBody: "As permitted by law, we may receive data from partners, providers and other independent sources, including public databases, to enable our service.",
    autoHeading: "Automatically collected data",
    autoBody: "We also automatically collect Wi-Fi connection data and browsing data (cookies).",
  },
  s2: {
    title: "2. How we use your personal data",
    tableHeader: ["Legal basis", "Purpose"],
    rows: [
      ["Performance of contracts or preliminary procedures (art. 7, V, LGPD)", "Receiving CVs; job interviews; hiring; customer prospecting."],
      ["Compliance with legal or regulatory obligation (art. 7, II, LGPD)", "Keeping Wi-Fi access records (art. 13 Brazilian Civil Internet Framework); keeping labor and social security documents."],
      ["Consent (art. 7, I, LGPD)", "Processing of children's data; external contacts of subject interest via electronic messages."],
    ],
  },
  s3: {
    title: "3. Who we share your personal data with",
    items: [
      "With service providers for hiring employees.",
      "With government bodies to fulfill legal obligations.",
      "In corporate transactions or changes involving the Controller, to ensure service continuity.",
      "Upon court order or request from competent administrative authorities.",
    ],
    tail: "All providers are subject to strict contracts that prohibit unauthorized use or disclosure of the personal data they access.",
  },
  s4: {
    title: "4. How we keep your personal data secure",
    items: [
      "Protection against unauthorized access to systems.",
      "Restricted access to storage locations by specific people.",
      "Strict confidentiality commitment from agents, employees and partners that handle data.",
      "Privacy governance program managed by the Data Protection Officer (DPO).",
    ],
    tail: "Although the Controller makes best efforts, no transmission is completely secure. In case of suspected security breach, we may totally or partially suspend services without prior notice and we guarantee transparency about what happened.",
  },
  s5: {
    title: "5. How long your data is stored",
    body: "Your data will be stored for the period strictly necessary for each purpose and/or according to the relevant legal and regulatory deadlines.",
  },
  s6: {
    title: "6. Your rights as a data subject and how to exercise them",
    items: [
      "Confirmation of processing.",
      "Access to data.",
      "Correction of incomplete, inaccurate or outdated data.",
      "Anonymization, blocking or deletion of unnecessary, excessive or non-compliantly processed data.",
      "Portability of data to another provider, upon express request.",
      "Deletion of data processed under consent.",
      "Information about public or private entities with which the Controller shared your data.",
      "Information about the possibility of not providing consent and its consequences.",
      "Revocation of consent.",
    ],
    portalLink: "Privacy Portal",
    via: "Requests are free and subject to identity validation. To exercise your rights, contact our DPO through the ",
    or: " or by email at ",
    rejection: "Your request may be legally rejected for formal reasons (e.g. inability to verify identity) or legal reasons (e.g. deletion request for data whose retention is a free exercise of a right). In such cases, we will provide reasonable justifications.",
  },
  s7: {
    title: "7. Applicable law and changes",
    body: "This document was prepared based on Brazilian Federal Law 13.709/2018 — General Data Protection Law (LGPD). The Controller reserves the right, at its sole discretion, to modify, alter, add or remove parts of this document at any time.",
    version: "Drafted on: 10/01/2023 · Updated on: 11/09/2023 · Version 1.0",
  },
  glossary: {
    title: "Glossary",
    items: [
      { term: "Anonymization", def: "Process by which data loses the possibility of being associated, directly or indirectly, with an individual, considering reasonable technical means available at the time of processing." },
      { term: "ANPD", def: "Brazilian National Data Protection Authority: public body responsible for overseeing, implementing and enforcing the LGPD nationwide." },
      { term: "Database", def: "Structured set of personal data, established in one or several locations, electronic or physical." },
      { term: "Controller", def: "Natural or legal person, of public or private law, responsible for decisions regarding the processing of personal data." },
      { term: "Consent", def: "Free, informed and unambiguous expression by which the data subject agrees with the processing of their personal data for a determined purpose." },
      { term: "Cookies", def: "Files sent by the server to the user's computer to identify them and obtain access data, allowing browsing to be personalized." },
      { term: "Personal data", def: "Any information related to an identified or identifiable natural person (name, address, email, IP, phone, taxpayer ID, among others)." },
      { term: "Sensitive personal data", def: "Special category of data concerning racial or ethnic origin, religious belief, political opinion, union membership, religion, health, sex life, genetic or biometric data." },
      { term: "IP", def: "Internet Protocol — set of numbers that identifies the user's computer on the internet." },
      { term: "Processor", def: "Natural or legal person that processes personal data on behalf of the controller." },
      { term: "Data subject", def: "Natural person to whom the personal data refers (customers, employees, partners and third parties)." },
      { term: "Processing", def: "Any operation performed with personal data: collection, production, reception, classification, use, access, reproduction, transmission, distribution, processing, archiving, storage, deletion, evaluation, control, modification, communication, transfer, dissemination or extraction." },
    ],
  },
  otherDocsTitle: "Other documents",
  policyLink: "Platform Privacy Policy",
  ombudsmanLink: "Reporting Channel",
};

const esContent: Content = {
  badge: "LGPD · Versión 1.0",
  titleA: "Aviso de ", titleHighlight: "Privacidad", titleEnd: " y Protección de Datos",
  meta: "Elaborado el 01/10/2023 · Actualizado el 09/11/2023.",
  intro: (
    <>
      <strong className="text-foreground">Barbalho Solutions Ltda.</strong>, CNPJ 43.406.507/0001-52, con sede en Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP — CP 06454-000 ("Controlador"), regula en este Aviso, de forma simple, transparente y objetiva, cómo se recopilarán, utilizarán y protegerán tus datos personales ("Titular").
    </>
  ),
  dpoCta: "Portal del Encargado de Protección de Datos (DPO)",
  summary: {
    heading: "Resumen",
    rows: [
      { k: "Agente de tratamiento", v: "Barbalho Solutions Ltda." },
      { k: "Rol en el tratamiento", v: "Controlador y Operador" },
      { k: "Naturaleza de los datos", v: "Datos personales proporcionados por el Titular, de naturaleza registral, financiera y sensible (art. 5, II de la Ley 13.709/2018)." },
      { k: "Compartición", v: "Operadores y proveedores esenciales; autoridades/órganos gubernamentales por exigencia legal o regulatoria." },
      { k: "Protección", v: "Medidas técnicas y administrativas adecuadas a la naturaleza de los datos tratados." },
      { k: "Tus derechos", v: "Confirmación, acceso, anonimización, corrección, eliminación, portabilidad, bloqueo, oposición y compartición." },
    ],
  },
  tocHeading: "Sumario",
  toc: [
    { id: "dados", label: "Qué datos recopilamos sobre ti" },
    { id: "uso", label: "Cómo utilizamos tus datos personales" },
    { id: "compartilhamento", label: "Con quién compartimos tus datos personales" },
    { id: "seguranca", label: "Cómo mantenemos tus datos seguros" },
    { id: "retencao", label: "Por cuánto tiempo se almacenan tus datos" },
    { id: "direitos", label: "Tus derechos como titular y cómo ejercerlos" },
    { id: "legislacao", label: "Legislación aplicable y cambios" },
    { id: "glossario", label: "Glosario" },
  ],
  s1: {
    title: "1. Qué datos recopilamos sobre ti",
    intro: "Para prestar servicios a nuestros clientes o contratar colaboradores, recopilamos algunas informaciones sobre ti:",
    tableHeader: ["Servicio", "Datos personales"],
    rows: [
      ["Recepción de currículos", "Nombre, edad, dirección, teléfono, correo, calificación profesional, currículo."],
      ["Entrevista de empleo", "Nombre, teléfono, calificación profesional, currículo."],
      ["Contratación de colaboradores", "Nombre, cónyuge y dependientes (si los hubiera), padre, madre, CPF, RG, CTPS, correo, fecha de nacimiento, dirección, teléfono, PIS, datos de salud (ASO), salario y datos bancarios."],
      ["Wi-Fi para clientes", "Dirección MAC, IP de conexión."],
      ["Prospección de clientes", "Nombre, teléfono y correo."],
    ],
    children: "Datos proporcionados directamente por ti",
    childrenNote: "En algunos casos será necesario recopilar datos de niños con vínculo parental contigo o bajo tu responsabilidad legal — en esos casos se requerirá consentimiento expreso. Es tu deber proporcionar información correcta y actualizada.",
    thirdHeading: "Datos proporcionados por terceros",
    thirdBody: "Según lo permita la ley, podemos recibir datos de socios, proveedores y otras fuentes independientes, incluidas bases de datos públicas, para viabilizar la prestación de nuestros servicios.",
    autoHeading: "Datos recopilados automáticamente",
    autoBody: "También recopilamos, de forma automática, datos de conexión Wi-Fi y datos de navegación (cookies).",
  },
  s2: {
    title: "2. Cómo utilizamos tus datos personales",
    tableHeader: ["Base legal", "Finalidad"],
    rows: [
      ["Ejecución de contratos o diligencias preliminares (art. 7, V, LGPD)", "Recepción de currículo; entrevista de empleo; contratación; prospección de clientes."],
      ["Cumplimiento de obligación legal o regulatoria (art. 7, II, LGPD)", "Guarda de registros de acceso al Wi-Fi (art. 13 del Marco Civil); guarda de documentos laborales y previsionales."],
      ["Consentimiento (art. 7, I, LGPD)", "Tratamiento de datos de niños; contactos externos de interés del titular vía mensajes electrónicos."],
    ],
  },
  s3: {
    title: "3. Con quién compartiremos tus datos personales",
    items: [
      "Con prestadores de servicio para la contratación de empleados.",
      "Con órganos gubernamentales para el cumplimiento de obligación legal.",
      "En transacciones o cambios societarios que involucren al Controlador, para la continuidad de los servicios.",
      "Mediante orden judicial o requerimiento de autoridades administrativas competentes.",
    ],
    tail: "Todos los prestadores están sujetos a contratos rigurosos que prohíben el uso o divulgación no autorizada de los datos personales a los que tienen acceso.",
  },
  s4: {
    title: "4. Cómo mantenemos tus datos personales seguros",
    items: [
      "Protección contra acceso no autorizado a los sistemas.",
      "Acceso restringido de personas específicas a los lugares de almacenamiento.",
      "Compromiso de sigilo absoluto por parte de agentes, empleados y socios que tratan datos.",
      "Programa de gobernanza en privacidad, gestionado por el Encargado de Protección de Datos Personales (DPO).",
    ],
    tail: "Aunque el Controlador adopta los mejores esfuerzos, ninguna transmisión es totalmente segura. En caso de sospecha de violación de seguridad, podemos suspender total o parcialmente los servicios sin aviso previo y garantizaremos transparencia sobre lo ocurrido.",
  },
  s5: {
    title: "5. Por cuánto tiempo se almacenarán tus datos",
    body: "Tus datos se almacenarán por el período estrictamente necesario para cada finalidad y/o conforme a los respectivos plazos legales y regulatorios.",
  },
  s6: {
    title: "6. Tus derechos como titular y cómo ejercerlos",
    items: [
      "Confirmación de la existencia del tratamiento.",
      "Acceso a los datos.",
      "Corrección de datos incompletos, inexactos o desactualizados.",
      "Anonimización, bloqueo o eliminación de datos innecesarios, excesivos o tratados en desconformidad.",
      "Portabilidad de los datos a otro proveedor, mediante solicitud expresa.",
      "Eliminación de los datos tratados con consentimiento.",
      "Información sobre las entidades públicas o privadas con las que el Controlador compartió tus datos.",
      "Información sobre la posibilidad de no otorgar consentimiento y sus consecuencias.",
      "Revocación del consentimiento.",
    ],
    portalLink: "Portal de Privacidad",
    via: "Las solicitudes son gratuitas y sometidas a validación de identidad. Para ejercer tus derechos, contacta a nuestro DPO mediante el ",
    or: " o por el correo ",
    rejection: "Tu solicitud puede ser legalmente rechazada por motivos formales (p. ej. imposibilidad de comprobación de identidad) o legales (p. ej. solicitud de eliminación de datos cuya conservación es libre ejercicio de un derecho). En esos casos, presentaremos justificaciones razonables.",
  },
  s7: {
    title: "7. Legislación aplicable y cambios",
    body: "Este documento fue elaborado con base en la Ley Federal nº 13.709/2018 — Ley General de Protección de Datos (LGPD). El Controlador se reserva el derecho, a su exclusivo criterio, de modificar, alterar, añadir o remover partes de este documento en cualquier momento.",
    version: "Elaborado el: 01/10/2023 · Actualizado el: 09/11/2023 · Versión 1.0",
  },
  glossary: {
    title: "Glosario",
    items: [
      { term: "Anonimización", def: "Proceso por el cual el dato pierde la posibilidad de asociación, directa o indirecta, a un individuo, considerados los medios técnicos razonables y disponibles en el momento del tratamiento." },
      { term: "ANPD", def: "Autoridad Nacional de Protección de Datos: órgano público responsable de velar, implementar y fiscalizar el cumplimiento de la LGPD en todo el territorio nacional." },
      { term: "Base de datos", def: "Conjunto estructurado de datos personales, establecido en uno o varios lugares, en soporte electrónico o físico." },
      { term: "Controlador", def: "Persona natural o jurídica, de derecho público o privado, a quien competen las decisiones referentes al tratamiento de datos personales." },
      { term: "Consentimiento", def: "Manifestación libre, informada e inequívoca por la cual el titular concuerda con el tratamiento de sus datos personales para una finalidad determinada." },
      { term: "Cookies", def: "Archivos enviados por el servidor al ordenador del usuario para identificarlo y obtener datos de acceso, permitiendo personalizar la navegación." },
      { term: "Dato personal", def: "Cualquier información relacionada a persona natural identificada o identificable (nombre, dirección, correo, IP, teléfono, CPF, entre otros)." },
      { term: "Dato personal sensible", def: "Categoría especial de datos referentes a origen racial o étnico, convicción religiosa, opinión política, afiliación sindical, religión, salud, vida sexual, datos genéticos o biométricos." },
      { term: "IP", def: "Internet Protocol — conjunto de números que identifica el ordenador del usuario en internet." },
      { term: "Operador", def: "Persona natural o jurídica que realiza el tratamiento de datos personales en nombre del controlador." },
      { term: "Titular", def: "Persona natural a quien se refieren los datos personales (clientes, colaboradores, socios y terceros)." },
      { term: "Tratamiento", def: "Toda operación realizada con datos personales: recolección, producción, recepción, clasificación, utilización, acceso, reproducción, transmisión, distribución, procesamiento, archivo, almacenamiento, eliminación, evaluación, control, modificación, comunicación, transferencia, difusión o extracción." },
    ],
  },
  otherDocsTitle: "Otros documentos",
  policyLink: "Política de Privacidad de la Plataforma",
  ombudsmanLink: "Canal de Denuncias",
};

const CONTENT: Record<Locale, Content> = { pt: ptContent, en: enContent, es: esContent };

export function AvisoPrivacidadePage() {
  const locale = useLocale();
  const c = CONTENT[locale];
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <FileLock2 className="size-3.5" /> {c.badge}
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            {c.titleA}<span className="gradient-text">{c.titleHighlight}</span>{c.titleEnd}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">{c.meta}</p>
          <p className="mt-5 max-w-3xl text-base text-muted-foreground">{c.intro}</p>
          <a
            href={DPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
          >
            {c.dpoCta} <ExternalLink className="size-3.5" />
          </a>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <nav aria-label={c.tocHeading} className="mb-12 rounded-2xl border border-border bg-card/40 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{c.tocHeading}</h2>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            {c.toc.map((i) => (
              <li key={i.id}><a className="hover:text-foreground" href={`#${i.id}`}>{i.label}</a></li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          <Section id="resumo" title={c.summary.heading}>
            <dl className="grid gap-4 sm:grid-cols-2">
              {c.summary.rows.map((r) => (
                <div key={r.k} className="rounded-xl border border-border bg-card/40 p-4">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{r.k}</dt>
                  <dd className="mt-1 text-sm text-foreground">{r.v}</dd>
                </div>
              ))}
            </dl>
          </Section>

          <Section id="dados" title={c.s1.title}>
            <p>{c.s1.intro}</p>
            <h3>{c.s1.children}</h3>
            <div className="overflow-x-auto">
              <table>
                <thead><tr><th>{c.s1.tableHeader[0]}</th><th>{c.s1.tableHeader[1]}</th></tr></thead>
                <tbody>
                  {c.s1.rows.map(([a, b]) => (
                    <tr key={a}><td>{a}</td><td>{b}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>{c.s1.childrenNote}</p>
            <h3>{c.s1.thirdHeading}</h3>
            <p>{c.s1.thirdBody}</p>
            <h3>{c.s1.autoHeading}</h3>
            <p>{c.s1.autoBody}</p>
          </Section>

          <Section id="uso" title={c.s2.title}>
            <div className="overflow-x-auto">
              <table>
                <thead><tr><th>{c.s2.tableHeader[0]}</th><th>{c.s2.tableHeader[1]}</th></tr></thead>
                <tbody>
                  {c.s2.rows.map(([a, b]) => (
                    <tr key={a}><td>{a}</td><td>{b}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="compartilhamento" title={c.s3.title}>
            <ol>{c.s3.items.map((it) => <li key={it}>{it}</li>)}</ol>
            <p>{c.s3.tail}</p>
          </Section>

          <Section id="seguranca" title={c.s4.title}>
            <ul>{c.s4.items.map((it) => <li key={it}>{it}</li>)}</ul>
            <p>{c.s4.tail}</p>
          </Section>

          <Section id="retencao" title={c.s5.title}>
            <p>{c.s5.body}</p>
          </Section>

          <Section id="direitos" title={c.s6.title}>
            <ul>{c.s6.items.map((it) => <li key={it}>{it}</li>)}</ul>
            <p>
              {c.s6.via}
              <a className="text-primary-glow hover:underline" href={DPO_URL} target="_blank" rel="noopener noreferrer">
                {c.s6.portalLink}
              </a>
              {c.s6.or}
              <a className="text-primary-glow hover:underline" href={`mailto:${KENTRO.email}`}>{KENTRO.email}</a>.
            </p>
            <p>{c.s6.rejection}</p>
          </Section>

          <Section id="legislacao" title={c.s7.title}>
            <p>{c.s7.body}</p>
            <p className="text-sm text-muted-foreground">{c.s7.version}</p>
          </Section>

          <Section id="glossario" title={c.glossary.title}>
            <dl className="space-y-4">
              {c.glossary.items.map((t) => (
                <div key={t.term}>
                  <dt className="font-semibold text-foreground">{t.term}</dt>
                  <dd className="text-sm text-muted-foreground">{t.def}</dd>
                </div>
              ))}
            </dl>
          </Section>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <h2 className="text-lg font-semibold">{c.otherDocsTitle}</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={locale === "pt" ? "/privacidade" : `/${locale}/privacidade`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              {c.policyLink}
            </a>
            <Link
              to="/ouvidoria"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              {c.ombudsmanLink}
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-muted-foreground [&_a]:text-primary-glow [&_h3]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:my-1 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 [&_strong]:text-foreground [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-lg [&_table]:border [&_table]:border-border [&_td]:border [&_td]:border-border [&_td]:p-3 [&_td]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-card/60 [&_th]:p-3 [&_th]:text-left [&_th]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}
